<template>
  <div
    v-quick-title="hoverTitle"
    class="schedule-event f-w v-centerer"
    :class="{ 'schedule-event--group': hasGroupedEvents }"
    :style="tileStyle"
  >
    <div v-if="hasGroupedEvents" class="schedule-event__left-bars v-centerer">
      <div v-for="(evt, idx) in event.groupedEvents" :key="idx" :style="{ backgroundColor: SColors[evt.color] }" />
    </div>
    <div class="schedule-event__content">
      <div v-if="!hasGroupedEvents" class="schedule-event__left-bar" :style="leftStyle" />
      <div class="schedule-event__time" style="text-align: left;">{{ timing }}</div>
      <div class="f-w schedule-event__name">
        {{ eventTitle }}
      </div>
      <div v-if="!hasGroupedEvents" :style="tagStyle" class="schedule-event__type">
        <div>{{ scarfLetters[event.type] }}</div>
      </div>
    </div>
    <div v-if="hasGroupedEvents" class="schedule-event__icon">
      <Icon rotate-left>{{ arrowDownLine }}</Icon>
    </div>
  </div>
</template>

<script>
/**
 * Component to display an event in a schedule.
 *
 * @vue-prop {Object} event - Event object { name, type, time, duration, color }
 */

// Components
import Icon from '@/components/icon.vue';
// Utils & assets
import { decodeHTML } from '@/utils/main';
import { SColors } from '@/utils/constants';
import { arrowDownLine } from '@/assets/icons/';

export default {

  name: 'ScheduleEvent',

  components: {
    Icon,
  },

  props: {
    event: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      SColors,
      arrowDownLine,
      scarfLetters: ['?', this.$t('message.MEETING_FL'), this.$t('message.GW_FL')],
      scarfTitles: ['?', this.$t('message.MEETING'), this.$t('message.GW')],
      scarfColors: ['#454545', 'hsl(220, 100%, 50%)', 'hsl(300, 100%, 50%)'],
    };
  },

  computed: {
    /** Boolean: Whether this event is a group of events */
    hasGroupedEvents() {
      return this.event.groupedEvents && this.event.groupedEvents.length;
    },
    /** String: Title of the event. If grouped, number of events in group */
    eventTitle() {
      return this.hasGroupedEvents
        ? this.$tc('message.N_EVENTS', this.event.groupedEvents.length, { nb: this.event.groupedEvents.length })
        : decodeHTML(this.event.name);
    },
    /** Moment: Time of the beginning of the event */
    startTime() {
      return this.event.time ? this.$moment(this.event.time, 'HH:mm') : this.$moment(this.event.date);
    },
    /** Moment: Time at the end of the event */
    endTime() {
      let duration = this.event.duration;
      if (this.event.groupedEvents && this.event.groupedEvents.length) {
        this.event.groupedEvents.forEach((e) => {
          const eEndTime = this.$moment(e.time, "HH:mm");
          eEndTime.add(e.duration, 's');
          duration = Math.max(duration, eEndTime.diff(this.startTime, 's'));
        });
      }
      return this.$moment(this.startTime).add(duration, 's');
    },
    /** String: Displayed time of the beginning and end of the event */
    timing() {
      return `${this.startTime.format('HH:mm')} - ${this.endTime.format('HH:mm')}`;
    },
    /** String: Color identifier for this element */
    realColor() {
      return this.hasGroupedEvents ? 'pdcaPending' : this.event.color;
    },
    /** Object: Style of this.$el */
    tileStyle() {
      let style = {};
      if (this.event.absolute) {
        style = {
          position: 'absolute',
          top: `${this.event.topPosition}px`,
          width: this.event.halfWidth ? 'calc(50% - 0.2rem)' : undefined,
          left: this.event.halfWidth && this.event.halfWidth.right ? 'calc(50% + 0.4rem)' : undefined,
          display: this.event.inGroup > 0 ? 'none' : undefined,
        };
      }
      return {
        ...style,
        backgroundColor: SColors[`${this.realColor}Bg`],
        border: `1px solid ${SColors[`${this.realColor}Hover`]}`,
      };
    },
    /** Object: Style of the left border of the tile */
    leftStyle() {
      return {
        backgroundColor: SColors[this.realColor],
      };
    },
    /** Object: Style of the type of the event */
    tagStyle() {
      return {
        backgroundColor: SColors[`${this.realColor}Medium`],
        color: SColors[this.realColor],
      };
    },
    hoverTitle() {
      return `${this.timing + this.$t('message.TWO_DOTS') + this.eventTitle}`;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.schedule-event {
  cursor: pointer;
  overflow: hidden;
  border-radius: 8px;
  position: relative;
  text-align: center;
  padding: .6rem .7rem;
  box-sizing: border-box;
  transition: transform 0.48s cubic-bezier(.64,-0.32,.41,1.57), z-index 0.48s linear;
  z-index: 0;

  &__content {
    flex: 1;
    min-width: 0px;
  }

  &__left-bar {
    position: absolute;
    left: 0px;
    top: 0px;
    bottom: 0px;
    width: 2px;
  }

  &__time {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__name {
    font-weight: 600;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    text-align: left;
    margin-top: .2rem;
    color: #001549;
    height: 1.3rem;
  }

  &__type {
    position: absolute;
    top: 5px;
    right: 5px;
    padding: 1px 3px;
    border-radius: 2px;
    font-size: 0.9rem;
  }

  &__left-bars {
    height: 100%;
    margin-right: 0.4rem;

    > div {
      width: 2px;
      height: 100%;
      margin-right: 3px;
    }
  }

  &__icon {
    .icon {
      width: .8rem;
      height: .8rem;
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .schedule-event {
    &__left-bar {
      width: 2px;
    }
  }
}
</style>
